/**
 * paio-ordering's resolved flow, read into the framework's `ResolvedFlow`
 * (ORDR-42).
 *
 * `GET /api/v1/ordering/flow/?entry=...&site=<uuid>` answers anonymously with
 * the contract section 9 body plus, for a site, `fulfilment_context` and
 * `disclosure` (ORDR-53).
 *
 * WHAT THIS APP TAKES FROM THE RESOLVER, AND NEVER FROM A LITERAL (ORDR-42
 * wire contract comment): the fulfilment mode, the channel and the location.
 * A typo such as "pickup" passes commerce's shape check and silently never
 * reaches a kitchen, so none of those values is written anywhere in `src/`.
 *
 * `fulfilment_context.firm_id` is INFORMATIONAL ONLY and is dropped here, so
 * nothing downstream can send it.
 *
 * STEPS. MENU, PAYMENT and TRACK are not questions a diner answers in the
 * flow form: the ordering surface itself is the menu, the payment and the
 * tracker. ALLERGEN is withdrawn (FR-DSC-007): the diner is no longer asked
 * to declare allergies; the menu discloses them instead. Every other step is
 * carried, REVIEW as the review kind, so the framework's "no REVIEW means
 * refuse" rule sees exactly what the server sent.
 */

import type { FlowStep, ResolvedFlow } from "@pirouz-ca/ui-components";

export interface FlowChannel {
  readonly type: string;
  readonly ref: string | null;
  readonly label: string | null;
}

/** What a FOOD cart's `fulfilment_context` is built from. No `firm_id`. */
export interface FulfilmentContext {
  readonly site_id: string;
  readonly location_node_id: string;
  readonly fulfilment_mode: string | null;
  readonly channel: FlowChannel;
}

export interface WireFlow {
  readonly flow: ResolvedFlow;
  readonly fulfilment: FulfilmentContext | null;
  readonly disclosure: string | null;
}

const SURFACE_STEPS = new Set(["MENU", "PAYMENT", "TRACK"]);

/**
 * Steps the resolver still sends that this app no longer presents. ALLERGEN:
 * FR-DSC-007 withdraws asking the diner to declare allergies. paio-ordering
 * `services/flow.py::_decide_allergen` still emits it (REQUIRED,
 * `optional_to_answer`); removing it there is a paio-ordering follow-up.
 */
const WITHDRAWN_STEPS = new Set(["ALLERGEN"]);

const LABELS: Readonly<Record<string, string>> = {
  LOCATION: "Where",
  MODE: "How you get your order",
  HANDOVER_DETAIL: "Where we bring it",
  SCHEDULE: "When",
  IDENTITY: "Your details",
  LOYALTY: "Rewards",
  REVIEW: "Review your order",
};

const STATES = new Set(["REQUIRED", "PREFILLED", "SKIPPED"]);

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function valueText(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    const entries = Object.values(value as Record<string, unknown>).filter(
      (entry) => typeof entry === "string" || typeof entry === "number",
    );
    if (entries.length === 1) {
      return String(entries[0]);
    }
    return JSON.stringify(value);
  }
  return undefined;
}

function parseChannel(raw: unknown): FlowChannel | null {
  const record = raw as Record<string, unknown> | null;
  const type = str(record?.["type"]);
  return type === null ? null : { type, ref: str(record?.["ref"]), label: str(record?.["label"]) };
}

/** The 200 body, or `null` when it is not a flow. */
export function flowFromWire(body: unknown): WireFlow | null {
  const data = (body as { data?: unknown } | null)?.data as Record<string, unknown> | null | undefined;
  if (!Array.isArray(data?.["steps"])) {
    return null;
  }
  const channel = parseChannel(data["channel"]);
  const steps: FlowStep[] = [];
  for (const raw of data["steps"] as unknown[]) {
    const step = raw as Record<string, unknown> | null;
    const id = str(step?.["id"]);
    const state = str(step?.["state"]);
    if (id === null || state === null || !STATES.has(state)) {
      return null;
    }
    if (SURFACE_STEPS.has(id) || WITHDRAWN_STEPS.has(id)) {
      continue;
    }
    const value = valueText(step?.["value"]);
    steps.push({
      id,
      label: LABELS[id] ?? id,
      kind: id === "REVIEW" ? "review" : "question",
      state: state as FlowStep["state"],
      ...(value === undefined ? {} : { value }),
    });
  }

  const context = data["fulfilment_context"] as Record<string, unknown> | null | undefined;
  const siteId = str(context?.["site_id"]);
  const locationNodeId = str(context?.["location_node_id"]);
  const mode = str(context?.["fulfilment_mode"]);
  const fulfilment =
    siteId !== null && locationNodeId !== null && channel !== null
      ? { site_id: siteId, location_node_id: locationNodeId, fulfilment_mode: mode, channel }
      : null;

  return {
    flow: {
      steps,
      venueFormat: str(data["venue_format"]) ?? "",
      channel: channel?.type ?? "",
      handoverMode: mode ?? "",
      ...(str(data["topology"]) === null ? {} : { topology: str(data["topology"]) as string }),
    },
    fulfilment,
    disclosure: str(data["disclosure"]),
  };
}
